import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import ConfirmationModal from 'components/global/ConfirmationModal';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListsInPage } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { usePrompt } from 'hooks/usePrompt';

import { ADVANCED_OMS, AUTOCOMPLETE, ORDER_ACTIONS, OTOFLEX } from 'services/consts';

import AppLogger from 'utils/AppLogger';

/**
 * @author @muhammedburakak
 */

/**
 * @description This component is used to assign driver to order or area
 * @param {string} ids - primary key of the row
 * @param {string} customIDs - the id will be show in the input with disabled for example orderIDs
 * @param {string} mode - mode of the form (area or order)
 */
const AssingToDriverForm = NiceModal.create(({ ids, customIDs, mode }) => {
	const modal = useModal();
	const dispatch = useDispatch();
	const axios = useAxiosPrivate();
	const defaultColSpan = {
		md: 6
	};
	const { showError, showSuccess } = useAlert();
	const title = mode === 'area' ? 'assignAreaToDriver' : 'assignToDriver';
	const showedIDsLabel = mode === 'area' ? 'areaID' : 'orderNumbers';

	const [labelAutocomplete, setLabelAutocomplete] = useState(null);
	const [loading, setLoading] = useState(false);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape(
					{
						driverID: yup
							.number()
							.nullable()
							.when('automatically', {
								is: null,
								then: (schema) => schema.required()
							}),
						automatically: yup.boolean().required()
					},
					['driverID', 'automatically']
				)
				.required(),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			driverID: null,
			automatically: null
		}
	});

	const {
		handleSubmit,
		setValue,
		watch,
		formState: { isDirty, isSubmitting, isSubmitSuccessful }
	} = formMethods;

	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);

	useEffect(() => {
		let showedIds = '';
		const mapper = customIDs || ids;
		mapper.forEach((id, index) => {
			showedIds += id + (index !== mapper.length - 1 ? ', ' : '');
		});
		setValue('showedIds', showedIds);
	}, []);

	const onSubmit = async (data) => {
		setLoading(true);
		try {
			const body =
				mode === 'area'
					? {
							areaIDs: ids,
							automatically: data.automatically ?? false
						}
					: {
							ids,
							automatically: data.automatically ?? false
						};

			if (!data.automatically) {
				body.driverID = data.driverID;
			}

			const URL = mode === 'area' ? ADVANCED_OMS.ASSIGN_AREA_TO_DRIVER : ORDER_ACTIONS.ASSIGN_TO_DRIVER_UPDATE;

			const response = await axios.post(URL, body);
			const { status } = response;
			if (status === 200) {
				const successfullIDs = response.data.successfulIncrementIds;
				const failedIDs = response.data.failedIncrementIds;
				if (failedIDs?.length > 0) {
					showError({ header: 'error.capacityFailedIDs', message: failedIDs.join(', ') });
					setLoading(false);
				}
				if (successfullIDs?.length > 0) {
					showSuccess({ header: 'capacitySuccessfullIDs', message: successfullIDs.join(', ') });
					setLoading(false);
				}
				if (mode === 'area') showSuccess({ header: 'areaAssignedHeader', message: 'areaAssignedMessage' });

				dispatch(updateDynamicListsInPage());
				modal.remove();
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		} finally {
			setLoading(false);
		}
	};

	const driverAutocompleteColumns = useMemo(() => [{ accessor: 'name', width: 200 }], []);
	const driverAutocompleteLabels = useMemo(() => ['name'], []);

	const automaticallySelectDriver = () => {
		setValue('automatically', true, { shouldValidate: true, shouldDirty: true });
		setLabelAutocomplete('automaticallySelectedDriver');
		setValue('driverID', null, { shouldValidate: true, shouldDirty: true });
	};

	const handleDriverChange = (value) => {
		setValue('driverID', value?.ID ?? null, { shouldValidate: true, shouldDirty: true });

		if (value) {
			setValue('automatically', false, { shouldValidate: true, shouldDirty: true });
			setLabelAutocomplete(undefined);
		}
	};

	return (
		<Modal maxWidth="medium" fullwidth className="modal-assignToDriver">
			<ModalTitle>
				<Typography variant="p1">{title}</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem colSpan={defaultColSpan} label="driver" required>
						<AutoComplete
							name="driverID"
							columns={driverAutocompleteColumns}
							selectionLabelAccessor={driverAutocompleteLabels}
							customValue={labelAutocomplete}
							firstItem={{
								action: automaticallySelectDriver,
								label: <Typography color="var(--blue)">automaticallySelectDriver</Typography>
							}}
							value={watch('driverID')}
							fetchFunction={
								mode === 'area'
									? undefined
									: async (props) => {
											const data = await axios
												.post(OTOFLEX.GET_ASSIGNABLE_DRIVERS, {
													orderIDs: ids
												})
												.catch((error) => {
													showError();
													AppLogger.error(error);
												});

											return data.data.filter((item) =>
												props?.keyword
													? item.name.toLowerCase().includes(props?.keyword.toLowerCase())
													: props?.IDs
														? props?.IDs.includes(item.ID)
														: true
											);
										}
							}
							setValue={handleDriverChange}
							autoCompleteName={mode === 'area' ? AUTOCOMPLETE.DRIVER_USERS : undefined}
						/>
					</FormItem>

					<FormItem label={showedIDsLabel} colSpan={defaultColSpan}>
						<StringInput name="showedIds" disabled={true} />
					</FormItem>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button
					label="cancel"
					onClick={() => {
						NiceModal.show(ConfirmationModal, {
							yesAction: () => {
								modal.remove();
							}
						});
					}}
					variant="outline"
				/>
				<Button
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					label="save"
					variant="blue"
					loading={loading}
					disabled={!isDirty}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
				/>
			</ModalActions>
		</Modal>
	);
});

export default AssingToDriverForm;
